import React from 'react';

import { FormattedMessage } from '@99designs/common/i18n';
import {
    getSquarespaceBrief,
    getSquarespaceBriefVariables,
} from '__generated__/getSquarespaceBrief';
import { SquarespaceBriefFields } from '__generated__/SquarespaceBriefFields';
import { AuthConsumer } from 'projects/components/AuthContext';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesignersPage from 'projects/routes/InviteDesignersPage';
import { BriefStep, StepState } from 'projects/util/step';
import { ApolloConsumer } from 'react-apollo';
import { RouteComponentProps, withRouter } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import { getIndustry } from './industry';
import queries from './SquarespaceBrief.graphql';
import SummaryPage from './SummaryPage';

type Props = RouteComponentProps<{
    briefId: string;
    step: string;
}>;

const steps: BriefStep<SquarespaceBriefFields>[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
        fields: [
            'title',
            'needs',
            'handlePayments',
            'description',
            'url',
            'assets',
            'inspirationUrls',
            'timeline',
        ],
    },
    {
        step: 2,
        title: __('About you'),
        fields: ['organizationSize', 'industry', 'organizationRole'],
    },
    { step: 3, title: __('Review your brief') },
    {
        step: 4,
        title: __('Invite designers to quote'),
        isAuthRequired: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

const SquarespaceBrief: React.FunctionComponent<Props> = props => (
    <BriefQuery<getSquarespaceBrief, getSquarespaceBriefVariables>
        query={queries.getSquarespaceBrief}
        variables={{ id: props.match.params.briefId }}
        fieldsTypename="SquarespaceBriefFields"
    >
        {(data, refetch) => {
            const brief = {
                ...data.brief,
                fields: data.brief.fields as SquarespaceBriefFields,
            };

            const description = () => {
                let selection = [brief.category && brief.category.title];

                const industry = getIndustry((brief.fields as SquarespaceBriefFields).industry);
                if (industry) {
                    selection = selection.concat(industry);
                }

                return (
                    <FormattedMessage
                        message={__(
                            "Based on your selection {selection} we've matched you with some awesome designers"
                        )}
                        values={{
                            selection: <strong>{selection.join(' / ')}</strong>,
                        }}
                    />
                );
            };

            const step = new StepState({
                steps,
                brief: data.brief,
                currentStep: parseInt(props.match.params.step, 10),
            });

            return (
                <ApolloConsumer>
                    {client => (
                        <AuthConsumer>
                            {({ login }) => (
                                <Brief
                                    refetch={refetch}
                                    schema="squarespace"
                                    brief={brief}
                                    viewer={data.viewer}
                                    login={login}
                                    stepState={step}
                                    client={client}
                                >
                                    {form => (
                                        <Switch>
                                            <Route
                                                path={`/briefs/squarespace/:briefId/1`}
                                                render={() => (
                                                    <DetailsPage brief={brief} form={form} />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/squarespace/:briefId/2`}
                                                render={() => (
                                                    <AboutYouPage form={form} brief={brief} />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/squarespace/:briefId/3`}
                                                render={() => <SummaryPage brief={brief} />}
                                            />
                                            <Route
                                                path={`/briefs/squarespace/:briefId/4`}
                                                render={() => (
                                                    <InviteDesignersPage
                                                        brief={brief}
                                                        viewer={data.viewer}
                                                    >
                                                        {description()}
                                                    </InviteDesignersPage>
                                                )}
                                            />
                                        </Switch>
                                    )}
                                </Brief>
                            )}
                        </AuthConsumer>
                    )}
                </ApolloConsumer>
            );
        }}
    </BriefQuery>
);

export default withRouter(SquarespaceBrief);
